import { Component, OnInit, ViewChild, OnDestroy, ViewContainerRef } from '@angular/core';
import { LightShowTrackService } from '../../_services/track.service';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { LightShowTrack } from '../../_models/LightShowTrack';
import { UserService } from '../../_services/user.service';
import { forkJoin } from 'rxjs';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { TrackCreateModalComponent } from './track-create-modal.component';
import { GenreEditModalComponent } from './genre-edit-modal.component';
import { TrackMetadataModalComponent } from './track-metadata-modal.component';
import { Genre } from '../../../app/_models/Genre';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpRequest, HttpResponse, HttpEventType, HttpClient } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { DialogService } from '../../_services/dialog.service';

@Component({
  selector: 'app-track-library',
  templateUrl: './track-library.component.html',
  styleUrls: ['./track-library.component.css']
})

export class TrackLibraryComponent implements OnInit, OnDestroy {

  public tracks: LightShowTrack[] = [];
  public tracksToShow: LightShowTrack[] = [];

  public searchString: string;
  public genresToFilter: Genre[];

  private bsModalRef: BsModalRef;
  public loading = false;

  private _hasSiteManagement = false;

  private onDestroy$: Subject<void> = new Subject();

  public downloadStates = {};

  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  @ViewChild('tracksTable') tracksTable: DatatableComponent;

  constructor(private trackService: LightShowTrackService,
              private userService: UserService,
              private modalService: BsModalService,
              private httpClient: HttpClient,
              private spinner: NgxSpinnerService,
              private dialogService: DialogService,
              private viewRef: ViewContainerRef) { }

  public genres: Genre[];

  ngOnInit() {
    this.trackService.genresUpdated
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => {
        this.loadGenres();
        this.loadTracks();
        this.clearFilters();
      }); // reload genres and tracks on add/remove

    this.trackService.trackGenresUpdated
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => {
        this.loadTracks(); // reload the tracks
        this.clearFilters();
      }); // reload tracks when their genres are updated

    this.loadTracks();

    this.loadGenres();
  }

  toggleVisibility(trackId: number) {
    this.tracks.find(t => t.trackId === trackId).visible = !this.tracks.find(t => t.trackId === trackId).visible;
    this.trackService.toggleTrackVisibility(trackId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  filterByGenres(filterByString?: boolean) {
    this.tracksToShow = this.tracks;

    if (filterByString) {
      this.filterByString();
    }

    if (this.genresToFilter && this.genresToFilter.length > 0) {
      this.genresToFilter.forEach(g => this.tracksToShow = this.tracksToShow.filter(t => JSON.stringify(t.genres).includes(JSON.stringify(g))));

      this.tracksTable.offset = 0;
    }
  }

  filterByString(filterByGenres?: boolean) {
    this.tracksToShow = this.tracks;

    if (filterByGenres) {
      this.filterByGenres();
    }

    const search = this.searchString ? this.searchString.toLowerCase() : "";
    if (this.searchString && this.searchString.trim().length > 0) {
      this.tracksToShow = this.tracksToShow.filter(t => 
        (t.title ? t.title.toLowerCase().indexOf(search) !== -1 : false) ||
        (t.artist ? t.artist.toLowerCase().indexOf(search) !== -1 : false) ||
        (t.createdBy ? t.createdBy.toLowerCase().indexOf(search) !== -1 : false) ||
        (t.duration ? t.duration.toString().toLowerCase().indexOf(search) !== -1 : false) ||
        (t.dateCreated ? t.dateCreated.toString().toLowerCase().indexOf(search) !== -1 : false) ||
        (t.trackId ? t.trackId.toString().indexOf(search) !== -1 : false));

      // Whenever the filter changes, always go back to the first page
      this.tracksTable.offset = 0;
    }
  }

  addNewTrack() {
    this.openUploadModal(null);
  }

  deleteTrack(track: LightShowTrack){
    this.dialogService.confirm('Are you sure?', 'This will permanently delete this library track.', this.viewRef)
      .then(res => {
        if (res){
          this.spinner.show();
          this.trackService.deleteTrack(track.trackId)
            .subscribe(_ => {
              this.spinner.hide();
              this.dialogService.alert('Success', 'That track has been successfully deleted.', this.viewRef);
              this.loadTracks();
            }, error => {
              this.spinner.hide();
              this.dialogService.alert('Uh oh!', `Something went wrong when trying to delete that track: ${error.error}`, this.viewRef);
            })
        }
      })
  }

  openMetadataModal(track?: any){
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    const initialState = {
      track: track,
    };

    this.bsModalRef = this.modalService
      .show(
        TrackMetadataModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-lg', initialState }));
  }

  openUploadModal(track?: any) {
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    const initialState = {
      viewRef: this.viewRef,
      track: track,
      genres: this.genres
    };

    this.bsModalRef = this.modalService
      .show(
        TrackCreateModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-lg', initialState }));
  }

  openGenreEditPanel() {
    this.bsModalRef = this.modalService.show(GenreEditModalComponent);
  }

  getTrackRowClass = (row) => {
    return {
      'track-library-row': true
    };
  }

  private clearFilters() {
    this.genresToFilter = [];
    this.searchString = '';
  }

  private loadGenres() {
    this.trackService.getAllGenres().subscribe(genres => {
      this.genres = genres;
    });
  }

  private loadTracks() {
    this.loading = true;
    this.spinner.show();
    
    const siteManagementObs = this.userService.hasSinglePermission('SiteManagement');
    const getUserTracksObs = this.trackService.getLightShowTracks();

    forkJoin([siteManagementObs, getUserTracksObs])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(results => {
        const sm: boolean = results[0];
        this._hasSiteManagement = sm;
        const tracks: LightShowTrack[] = results[1];
        this.tracks = [];
        this.tracksToShow = [];

        tracks.forEach(track => {
          if (track.visible === true || sm) {
            this.tracks.push(track);
            this.tracksToShow.push(track);
          }
        });

        this.loading = false;
        this.spinner.hide();
      }, error => {
        alert(error.message);
        this.loading = false;
        this.spinner.hide();
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }



  public checkDownloadLink(track: LightShowTrack, rowIndex: number) {
    this.downloadStates[rowIndex + '-download'] = 1;
    this.downloadStates[rowIndex + '-downloadPercent'] = 0;

    const req = new HttpRequest('GET', track.trackUrl, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob'
    });

    this.httpClient.request<any>(req).subscribe(event => {
      // this.downloadStates[rowIndex + '-download'] = 2;
      // this.downloadStates[rowIndex + '-downloadPercent'] = 0;
      // Access event stream to report progress
      if (event.type === HttpEventType.DownloadProgress) {
        // Calculate percentage done from the 'Content-Size'
        const percentDone = Math.round(100 * event.loaded / event.total);
        this.downloadStates[rowIndex + '-downloadPercent'] = percentDone;
      } else if (event instanceof HttpResponse) {
        const url = window.URL.createObjectURL(event.body);
        const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
        a.href = url;
        a.download = track.trackUrl.split('/').pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.downloadStates[rowIndex + '-download'] = 3;
      }
    });


  }
}
